import { BRAND } from '@console/shared'
import { useQueryClient } from '@tanstack/react-query'
import { BookOpen, Mail, Users } from 'lucide-react'
import { useState } from 'react'
import { DocsLink } from '../components/DocsLink'
import { Button } from '../components/ui/Button'
import { ErrorText, Field } from '../components/ui/misc'
import { api, useMe, type AccessRequest } from '../lib/api'
import { fmtDate } from '../lib/format'

/**
 * Invite-only beta: someone signed in whose access request is still being reviewed (or was declined). They can say
 * what they're building, read the docs, and get in at once through a teammate's invitation.
 */
export function PendingAccess() {
  const { data: me } = useMe()
  const req = me?.accessRequest
  const rejected = me?.access === 'rejected'
  const email = me?.user?.email ?? ''

  return (
    <div className="h-full overflow-y-auto px-4">
      <div className="mx-auto w-full max-w-md py-16">
        <p className="eyebrow">Private beta</p>
        <h1 className="headline mt-3 text-[28px] leading-tight">
          {rejected ? (
            <>
              <span className="pre">Thanks for your interest.</span>
              We can't offer access right now.
            </>
          ) : (
            <>
              <span className="pre">You're on the list.</span>
              We'll email you the moment you're in.
            </>
          )}
        </h1>

        <div className="mt-6 flex items-center gap-3 border-y border-line-subtle py-3">
          <Mail className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.6} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13.5px] font-medium text-ink">{email}</div>
            <div className="text-[12px] text-ink-muted">{req ? `Requested ${fmtDate(req.createdAt)}` : 'Requested just now'}</div>
          </div>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${rejected ? 'bg-surface-sunken text-ink-muted' : 'bg-brand/10 text-brand'}`}>
            {rejected ? 'Not now' : 'In review'}
          </span>
        </div>

        {rejected ? (
          <p className="mt-4 text-[13px] leading-relaxed text-ink-secondary">
            We're letting teams in gradually. If you think this is a mistake, write to{' '}
            <a className="font-medium text-brand" href={`mailto:${BRAND.supportEmail}`}>
              {BRAND.supportEmail}
            </a>
            .
          </p>
        ) : (
          <Details initial={req} />
        )}

        <div className="mt-4 divide-y border-y border-line-subtle divide-[color:var(--border-subtle)]">
          <div className="flex gap-3 px-1 py-3">
            <Users className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.6} />
            <p className="text-[13px] leading-relaxed text-ink-secondary">
              <span className="font-medium text-ink">Joining a teammate's organization?</span> Ask them to invite{' '}
              <span className="font-medium text-ink">{email}</span> from Settings → Members. Invitations skip the wait.
            </p>
          </div>
          <DocsLink className="flex items-center gap-3 px-1 py-3 hover:bg-surface-hover">
            <BookOpen className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.6} />
            <span className="text-[13px] text-ink-secondary">
              <span className="font-medium text-ink">Read the API guide</span> while you wait
            </span>
          </DocsLink>
        </div>
      </div>
    </div>
  )
}

/** What they're building: optional, shown to the team reviewing requests. */
function Details({ initial }: { initial?: AccessRequest }) {
  const qc = useQueryClient()
  const given = Boolean(initial?.company || initial?.useCase)
  const [editing, setEditing] = useState(!given)
  const [form, setForm] = useState({ company: initial?.company ?? '', useCase: initial?.useCase ?? '' })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<unknown>(null)

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      await api('/api/access-request', { body: { company: form.company || undefined, useCase: form.useCase || undefined } })
      await qc.invalidateQueries({ queryKey: ['me'] })
      setEditing(false)
    } catch (e2) {
      setErr(e2)
    } finally {
      setBusy(false)
    }
  }

  if (!editing)
    return (
      <div className="mt-4 border-b border-line-subtle pb-3">
        <div className="flex items-center justify-between">
          <span className="text-[12px] font-medium text-ink-muted">What you told us</span>
          <button type="button" className="text-[12px] font-medium text-brand" onClick={() => setEditing(true)}>
            Edit
          </button>
        </div>
        {initial?.company && <div className="mt-1.5 text-[13.5px] font-medium text-ink">{initial.company}</div>}
        {initial?.useCase && <p className="mt-1 whitespace-pre-wrap text-[13px] leading-relaxed text-ink-secondary">{initial.useCase}</p>}
      </div>
    )

  return (
    <form onSubmit={save} className="mt-4 space-y-3 border-b border-line-subtle pb-4">
      <div>
        <div className="text-[13.5px] font-medium text-ink">Help us get you in sooner</div>
        <p className="mt-0.5 text-[12.5px] text-ink-muted">Optional. The team reviewing requests reads this.</p>
      </div>
      <Field label="Company">
        <input className="input" value={form.company} maxLength={120} onChange={(e) => setForm({ ...form, company: e.target.value })} />
      </Field>
      <Field label="What will you use it for?" hint="Evals, routing, guardrails, tool choice… a sentence is plenty.">
        <textarea
          className="input min-h-[76px]"
          value={form.useCase}
          maxLength={2000}
          onChange={(e) => setForm({ ...form, useCase: e.target.value })}
        />
      </Field>
      <ErrorText error={err} />
      <div className="flex justify-end gap-2">
        {given && (
          <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        )}
        <Button type="submit" loading={busy} disabled={!form.company.trim() && !form.useCase.trim()}>
          Send
        </Button>
      </div>
    </form>
  )
}
